import {
	Alert,
	Box,
	Button,
	CheckboxInput,
	Divider,
	Heading,
	Icons,
	Inline,
	joinAttributes,
	RadioInput,
	RelativeTime,
	Select,
	Spinner,
	Stack,
	Text,
	Tooltip,
} from "@sembark-travel/ui-base"
import { Dialog, useDialog } from "@sembark-travel/ui-dialog"
import {
	addKeyToFieldArrayItem,
	arrayMutators,
	FieldArray,
	FieldArrayErrorMessage,
	Form,
	getFieldArrayItemKey,
	GetFieldValue,
	MarkdownInputField,
	SelectField,
	SubmissionError,
	TextInputField,
	useFieldValue,
	useForm,
	validateFormValues,
	withServerErrors,
} from "@sembark-travel/ui-form"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import useSWR from "swr"
import * as Validator from "yup"
import { isBefore, isSameOrAfter } from "@sembark-travel/datetime-utils"
import {
	customerPaymentPreferencesXHR,
	formatRuleConditionLabel,
	formatRuleInstalmentLabels,
	InstalmentShareBar,
	isFallbackRule,
	MinimumAmountBadge,
	NewPaymentRuleInDialog,
	TCustomerPaymentPreference,
} from "../CustomerPaymentPreferences"
import {
	formValuesToPayload,
	getPresetReferenceIds,
	hasFallbackRule,
	isSameRule,
	keepFallbackRuleLast,
	makeEmptyRuleBlock,
	moveRule,
	PAYMENT_POLICY_PRESETS,
	presetToRulePayload,
	putRuleFirst,
	TPaymentPolicyPresetId,
	TPaymentPolicyRuleBlock,
} from "./utils"
import { paymentPoliciesXHR } from "./store"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { useXHR } from "@sembark-travel/xhr"
import { useNavigate } from "../router-utils"
import { AddDateRangeRuleButton, DateRangeDialog } from "../shared/Policy"
import {
	dateRangeSchema,
	formatDateRange,
	type TDateRange,
} from "../shared/policies"

export type TNewPaymentPolicyFormSchema = {
	name: string
	base_rule: TPaymentPolicyRuleBlock
	rules: Array<TPaymentPolicyRuleBlock>
	text: string
}

const validationSchema = Validator.object().shape({
	name: Validator.string()
		.min(4, "Policy name cannot be shorter than 4 characters")
		.max(90, "Policy name cannot be longer than 90 characters")
		.required("Policy name is required"),
	text: Validator.string()
		.optional()
		.max(10000, "Keep the text under 10000 characters"),
	base_rule: Validator.object().shape({
		rules: Validator.array()
			.test(
				"has-fallback-rule",
				"Base rules must include a Fallback Rule",
				(rules) =>
					hasFallbackRule(
						rules as Array<TCustomerPaymentPreference> | undefined
					)
			)
			.min(1, "Add at least one rule"),
	}),
	rules: Validator.array()
		.of(
			dateRangeSchema.shape({
				rules: Validator.array().min(1, "Add at least one rule"),
			})
		)
		.test(
			"no-overlapping-date-ranges",
			"Date ranges must not overlap",
			function (rules) {
				if (!rules || rules.length < 2) return true
				const filled = rules
					.map((r, index) => ({ index, from: r?.valid_from, to: r?.valid_to }))
					.filter(
						(r): r is { index: number; from: Date; to: Date } =>
							Boolean(r.from) && Boolean(r.to)
					)
					.sort((a, b) =>
						isBefore(a.from, b.from) ? -1 : isBefore(b.from, a.from) ? 1 : 0
					)

				for (let i = 0; i < filled.length - 1; i++) {
					const current = filled[i]
					const next = filled[i + 1]
					// Dates are picked in a dialog which already prevents overlaps, so
					// this only guards the saved policies
					if (isSameOrAfter(current.to, next.from, "day")) {
						return this.createError({
							message: `${formatDateRange(next.from, next.to)} overlaps with ${formatDateRange(current.from, current.to)}`,
						})
					}
				}
				return true
			}
		),
})

// Rules of a block apply in order, so a second Fallback Rule is never
// reached.
function hasAtMostOneFallbackRule(rules?: Array<unknown>) {
	return (
		((rules || []) as Array<TCustomerPaymentPreference>).filter(isFallbackRule)
			.length <= 1
	)
}

type TRuleSelectFormSchema = {
	rules: Array<TCustomerPaymentPreference>
}

function makeRuleSelectSchema(requireFallbackRule?: boolean) {
	return Validator.object().shape({
		rules: Validator.array()
			.test(
				"single-fallback-rule",
				"Only one Fallback Rule is allowed",
				hasAtMostOneFallbackRule
			)
			.test(
				"has-fallback-rule",
				"You must include one Fallback Rule",
				(rules) =>
					!requireFallbackRule ||
					hasFallbackRule(
						rules as Array<TCustomerPaymentPreference> | undefined
					)
			)
			.min(1, "Select at least one rule"),
	})
}

export function NewPaymentPolicy() {
	const navigate = useNavigate()
	const xhr = useXHR()
	return (
		<NewPaymentPolicyForm
			onSubmit={async (values) => {
				await paymentPoliciesXHR(xhr).create(formValuesToPayload(values))
				showSnackbar("Payment policy created successfully")
				navigate("/org/payment-policies")
			}}
			onCancel={() => navigate("/org/payment-policies")}
		/>
	)
}

function NewDefaultPaymentPolicy({
	baseRule,
	onSuccess,
	onBack,
}: {
	// the rule of the picked preset, none for a custom policy
	baseRule: TCustomerPaymentPreference | null
	onSuccess: () => void
	onBack: () => void
}) {
	const xhr = useXHR()
	const initialValues = useMemo<TNewPaymentPolicyFormSchema>(
		() => ({
			name: "",
			text: "",
			base_rule: {
				...makeEmptyRuleBlock(),
				rules: baseRule ? [baseRule] : [],
			},
			rules: [],
		}),
		[baseRule]
	)

	return (
		<NewPaymentPolicyForm
			initialValues={initialValues}
			isNameHidden
			onCancel={onBack}
			cancelLabel="Back"
			submitLabel={
				<>
					<Icons.Ok /> Next
				</>
			}
			onSubmit={async (values) => {
				// the backend names the policy and marks the first one as default
				const { name: _name, ...payload } = formValuesToPayload(values)
				await paymentPoliciesXHR(xhr).create(payload)
				onSuccess()
			}}
		/>
	)
}

type TPaymentPolicyPresetSelection = {
	preset: TPaymentPolicyPresetId | "custom"
	// the Fallback Rule of the picked preset, null for a custom policy
	rule: TCustomerPaymentPreference | null
}

export function OnboardingPaymentPolicy({
	onSuccess,
}: {
	onSuccess: () => void
}) {
	const xhr = useXHR()
	const [selection, setSelection] =
		useState<TPaymentPolicyPresetSelection | null>(null)
	// keeps the picked option selected when going back to the presets
	const [lastPreset, setLastPreset] =
		useState<TPaymentPolicyPresetSelection["preset"]>()
	const presetInitialValues = useMemo<TPaymentPolicyPresetFormSchema>(
		() => ({ preset: lastPreset }),
		[lastPreset]
	)

	async function selectPreset(
		presetId: TPaymentPolicyPresetSelection["preset"]
	) {
		if (presetId === "custom") {
			setLastPreset(presetId)
			setSelection({ preset: presetId, rule: null })
			return
		}
		const preset = PAYMENT_POLICY_PRESETS.find((p) => p.id === presetId)
		if (!preset) throw new Error("Unknown payment policy preset")
		const api = customerPaymentPreferencesXHR(xhr)
		const references = await api.getReferences().then((resp) => resp.data)
		const { referenceIds, missing } = getPresetReferenceIds(preset, references)
		if (!referenceIds) {
			throw new Error(
				`Couldn't find the ${missing.join(", ")} payment reference. Please create a custom policy.`
			)
		}
		// the api returns the existing rule when one with the same details exists
		const rule = await api.create(presetToRulePayload(preset, referenceIds))
		setLastPreset(presetId)
		setSelection({ preset: presetId, rule })
	}

	return (
		<>
			<Stack gap="2">
				<Heading as="h3">Set Default Payment Policy</Heading>
				<Text>
					Define payment milestones for bookings. This policy applies to all
					bookings by default. <b>You can make changes to this later also.</b>
					{!selection
						? " Choose from the preset payment policies below or create a custom policy to suit your booking requirements."
						: null}
				</Text>
			</Stack>
			<Divider />
			{!selection ? (
				<PaymentPolicyPresetForm
					initialValues={presetInitialValues}
					onSubmit={async ({ preset }) => {
						if (preset) await selectPreset(preset)
					}}
				/>
			) : (
				<NewDefaultPaymentPolicy
					baseRule={selection.rule}
					onBack={() => setSelection(null)}
					onSuccess={onSuccess}
				/>
			)}
		</>
	)
}

type TPaymentPolicyPresetFormSchema = {
	preset?: TPaymentPolicyPresetSelection["preset"]
}

const PRESET_OPTIONS: Array<{
	id: TPaymentPolicyPresetSelection["preset"]
	label: string
}> = [
	...PAYMENT_POLICY_PRESETS.map(({ id, label }) => ({ id, label })),
	{ id: "custom", label: "Custom" },
]

function PaymentPolicyPresetForm({
	initialValues,
	onSubmit,
}: {
	initialValues?: TPaymentPolicyPresetFormSchema
	onSubmit: (values: TPaymentPolicyPresetFormSchema) => Promise<void>
}) {
	return (
		<Form<TPaymentPolicyPresetFormSchema>
			initialValues={initialValues}
			onSubmit={withServerErrors(onSubmit)}
			subscription={{ submitting: true }}
		>
			{({ submitting, handleSubmit }) => (
				<form noValidate onSubmit={handleSubmit}>
					<GetFieldValue<
						TPaymentPolicyPresetFormSchema["preset"]
					> name="preset">
						{({ value, onChange }) => (
							<Stack gap="4">
								<Stack gap="2">
									<Text>Choose a preset or create your own</Text>
									<Inline gap="4" flexWrap="wrap">
										{PRESET_OPTIONS.map((option) => {
											const isSelected = value === option.id
											return (
												<Box
													key={option.id}
													as="label"
													rounded="md"
													borderWidth="1"
													borderColor={
														isSelected ? "primary_emphasis" : "default"
													}
													bgColor={isSelected ? "primary" : undefined}
													cursor="pointer"
												>
													<Inline
														paddingX="4"
														paddingY="2"
														gap="2"
														alignItems="center"
													>
														<RadioInput
															name="preset"
															value={option.id}
															checked={isSelected}
															disabled={submitting}
															onChange={() => onChange(option.id)}
														/>
														<Text fontWeight="semibold">{option.label}</Text>
													</Inline>
												</Box>
											)
										})}
									</Inline>
								</Stack>
								<SubmissionError />
								<Inline>
									<Button
										type="submit"
										status="primary"
										level="primary"
										disabled={!value || submitting}
										style={{ opacity: !value ? 0.5 : 1 }}
									>
										{submitting ? <Icons.Spinner /> : <Icons.Ok />}{" "}
										{submitting ? "Saving..." : "Next"}
									</Button>
								</Inline>
							</Stack>
						)}
					</GetFieldValue>
				</form>
			)}
		</Form>
	)
}

export function NewPaymentPolicyForm({
	onSubmit,
	onCancel,
	initialValues: propInitialValues,
	isEdit,
	isDefault,
	isNameHidden = false,
	submitLabel,
	cancelLabel = "Cancel",
}: {
	onSubmit: (values: TNewPaymentPolicyFormSchema) => Promise<unknown>
	onCancel?: () => void
	initialValues?: TNewPaymentPolicyFormSchema
	isEdit?: boolean
	isDefault?: boolean
	isNameHidden?: boolean
	submitLabel?: React.ReactNode
	cancelLabel?: React.ReactNode
}) {
	const initialValues = useMemo<TNewPaymentPolicyFormSchema>(
		() =>
			propInitialValues || {
				name: "",
				text: "",
				base_rule: makeEmptyRuleBlock(),
				rules: [],
			},
		[propInitialValues]
	)

	return (
		<Form<TNewPaymentPolicyFormSchema>
			initialValues={initialValues}
			validate={validateFormValues(
				isNameHidden ? validationSchema.omit(["name"]) : validationSchema
			)}
			onSubmit={withServerErrors(onSubmit)}
			subscription={{ submitting: true }}
			mutators={{ ...arrayMutators }}
		>
			{({ submitting, handleSubmit, form }) => (
				<form noValidate onSubmit={handleSubmit}>
					<Stack gap="4">
						{!isNameHidden ? (
							<TextInputField
								label="Name"
								name="name"
								placeholder="e.g. Default Payment Policy"
								type="text"
								maxWidth="xs"
								disabled={isDefault}
							/>
						) : null}
						<RuleBlock name="base_rule" isBase />
						<FieldArray<TPaymentPolicyRuleBlock>
							name="rules"
							subscription={{ value: true }}
						>
							{({ fields }) => {
								const blocks: Array<TPaymentPolicyRuleBlock> =
									fields.value || []
								return (
									<Stack gap="4">
										{fields.map((blockName, blockIndex) => (
											<RuleBlock
												key={getFieldArrayItemKey(form as never, blockName)}
												name={blockName}
												isBase={false}
												validFrom={blocks[blockIndex]?.valid_from}
												validTo={blocks[blockIndex]?.valid_to}
												otherDateRanges={blocks.filter(
													(_, index) => index !== blockIndex
												)}
												onChangeDates={(dates) =>
													fields.update(blockIndex, {
														...blocks[blockIndex],
														...dates,
													})
												}
												onRemoveRule={() => fields.remove(blockIndex)}
											/>
										))}
										<AddDateRangeRuleButton
											existingDateRanges={blocks}
											onAdd={(dates) =>
												fields.push(
													addKeyToFieldArrayItem(makeEmptyRuleBlock(dates))
												)
											}
										/>
									</Stack>
								)
							}}
						</FieldArray>
						<Divider marginY="2" />
						<MarkdownInputField
							label="Additional information"
							name="text"
							placeholder="Any special remarks."
							secondaryLabel="Optional"
							rows={4}
						/>
						<Divider sm />
						<SubmissionError />
						<Inline gap="4">
							<Button
								type="submit"
								disabled={submitting}
								status="primary"
								level="primary"
								style={{ opacity: submitting ? 0.5 : 1 }}
							>
								{submitting ? (
									<Box>
										<Icons.Spinner />
									</Box>
								) : null}
								{submitting
									? isEdit
										? "Updating..."
										: "Saving..."
									: submitLabel || (isEdit ? "Update Policy" : "Save Policy")}
							</Button>
							{onCancel ? (
								<Button
									onClick={onCancel}
									level="tertiary"
									disabled={submitting}
									style={{ opacity: submitting ? 0.5 : 1 }}
								>
									{cancelLabel}
								</Button>
							) : null}
						</Inline>
					</Stack>
				</form>
			)}
		</Form>
	)
}

export function RuleBlock({
	name,
	isBase,
	validFrom,
	validTo,
	otherDateRanges,
	onChangeDates,
	onRemoveRule,
	readOnly = false,
	rules: rulesProp,
}: {
	name?: string
	isBase: boolean
	validFrom?: Date | null
	validTo?: Date | null
	// ranges of the other blocks, which this one must not overlap with
	otherDateRanges?: Array<TDateRange>
	onChangeDates?: (dates: TDateRange) => void
	onRemoveRule?: () => void
	readOnly?: boolean
	rules?: Array<TCustomerPaymentPreference>
}) {
	if (rulesProp !== undefined) {
		return (
			<RuleBlockView
				isBase={isBase}
				validFrom={validFrom}
				validTo={validTo}
				rules={rulesProp}
				readOnly={readOnly}
				otherDateRanges={otherDateRanges}
				onChangeDates={onChangeDates}
				onRemoveRule={onRemoveRule}
			/>
		)
	}

	if (!name) {
		throw new Error("RuleBlock requires either `name` or `rules` prop")
	}

	return (
		<RuleBlockFormField
			name={name}
			isBase={isBase}
			validFrom={validFrom}
			validTo={validTo}
			otherDateRanges={otherDateRanges}
			onChangeDates={onChangeDates}
			onRemoveRule={onRemoveRule}
			readOnly={readOnly}
		/>
	)
}

function RuleBlockFormField({
	name,
	...props
}: Omit<
	React.ComponentProps<typeof RuleBlockView>,
	"rules" | "onUpdateRules"
> & {
	name: string
}) {
	const form = useForm()

	return (
		<GetFieldValue<Array<TCustomerPaymentPreference>> name={`${name}.rules`}>
			{({ value }) => {
				const rules = value || []
				return (
					<Stack gap="2">
						<RuleBlockView
							{...props}
							rules={rules}
							onUpdateRules={(newRules) => {
								form.change(`${name}.rules`, newRules)
							}}
						/>
						<FieldArrayErrorMessage name={`${name}.rules`} />
					</Stack>
				)
			}}
		</GetFieldValue>
	)
}

function RuleBlockView({
	isBase,
	validFrom,
	validTo,
	rules,
	readOnly = false,
	otherDateRanges,
	onChangeDates,
	onRemoveRule,
	onUpdateRules,
}: {
	isBase: boolean
	validFrom?: Date | null
	validTo?: Date | null
	rules: Array<TCustomerPaymentPreference>
	readOnly?: boolean
	otherDateRanges?: Array<TDateRange>
	onChangeDates?: (dates: TDateRange) => void
	onRemoveRule?: () => void
	onUpdateRules?: (rules: Array<TCustomerPaymentPreference>) => void
}) {
	const [isEditingDates, editDates, stopEditingDates] = useDialog()
	const [isRemovingBlock, askToRemoveBlock, cancelRemoveBlock] = useDialog()
	const [isAddingRules, startAddingRules, stopAddingRules] = useDialog()

	return (
		<Stack borderWidth="1" borderColor="emphasis" rounded="md">
			{!isBase ? (
				<Inline
					padding="3"
					gap="2"
					roundedTop="md"
					alignItems="center"
					justifyContent="between"
					flexWrap="wrap"
					bgColor="primary"
					borderBottomWidth="1"
				>
					<Text fontWeight="semibold" fontSize="md">
						{formatDateRange(validFrom, validTo)} (Check-in)
					</Text>
					<Inline
						gap="2"
						alignItems="center"
						flexWrap="wrap"
						flex="1"
						justifyContent="between"
					>
						{!readOnly && onChangeDates ? (
							<Button
								inline
								onClick={editDates}
								title="Edit the check-in dates"
							>
								<Icons.Pencil />
							</Button>
						) : null}
						{!readOnly && onRemoveRule ? (
							<Button onClick={askToRemoveBlock} size="sm" level="tertiary">
								<Icons.Cancel /> Remove
							</Button>
						) : null}
					</Inline>
				</Inline>
			) : (
				<Inline
					padding="3"
					gap="2"
					alignItems="center"
					bgColor="success"
					borderBottomWidth="1"
					roundedTop="md"
				>
					<Text fontSize="md" fontWeight="semibold">
						Base Rules
					</Text>
					<Tooltip content="Applies when no date range matches the check-in date">
						<Box>
							<Icons.Info />
						</Box>
					</Tooltip>
				</Inline>
			)}
			<Box
				padding="4"
				paddingTop="0"
				paddingBottom="2"
				bgColor="white"
				roundedBottom="md"
			>
				<Stack gap="4">
					{!rules.length ? (
						<Stack gap="3" alignItems="center" paddingY="6">
							<Icons.Info color="muted" size="6" />
							<Text color="muted">There are currently no rules.</Text>
							{!readOnly ? (
								<Button level="secondary" onClick={startAddingRules}>
									<Icons.Plus /> Add Rule
								</Button>
							) : null}
						</Stack>
					) : (
						<Stack gap="2">
							<Stack as="ul" rounded="md">
								{rules.map((rule, idx) => (
									<Inline
										as="li"
										key={rule.id}
										gap="4"
										padding="3"
										alignItems="center"
										justifyContent="between"
										flexWrap="wrap"
										borderBottomWidth={
											idx === rules.length - 1 && readOnly ? "0" : "1"
										}
									>
										<Inline
											flexWrap="nowrap"
											gap="4"
											style={{ maxWidth: "50%" }}
										>
											<Box
												size="4"
												rounded="full"
												borderWidth="1"
												borderColor="black"
												display="flex"
												alignItems="center"
												justifyContent="center"
												flexShrink="0"
												marginTop="1"
											>
												<Text fontSize="xs" color="black">
													{idx + 1}
												</Text>
											</Box>
											<Box flex="1">
												<RuleDetails rule={rule} />
											</Box>
										</Inline>
										<Box flex="1" style={{ minWidth: "40%" }} maxWidth="lg">
											<InstalmentShareBar breakdowns={rule.breakdowns} />
										</Box>
									</Inline>
								))}
							</Stack>
							{!readOnly ? (
								<Inline>
									<Button
										level="secondary"
										size="sm"
										onClick={startAddingRules}
									>
										<Icons.Plus /> Add Rule
									</Button>
								</Inline>
							) : null}
						</Stack>
					)}
					{!readOnly ? (
						<RuleSelectDialog
							open={isAddingRules}
							title={
								isBase
									? "Base Rules"
									: `${formatDateRange(validFrom, validTo)} (Check-in)`
							}
							requireFallbackRule={isBase}
							selectedRules={rules}
							onClose={stopAddingRules}
							onSuccess={(selected) => {
								onUpdateRules?.(selected)
								stopAddingRules()
							}}
						/>
					) : null}
				</Stack>
			</Box>
			{!readOnly && onChangeDates ? (
				<DateRangeDialog
					open={isEditingDates}
					title="Edit Date"
					submitLabel="Update"
					existingDateRanges={otherDateRanges}
					initialValues={{
						valid_from: validFrom || null,
						valid_to: validTo || null,
					}}
					onSuccess={(dates) => {
						onChangeDates(dates)
						stopEditingDates()
					}}
					onClose={stopEditingDates}
				/>
			) : null}
			{!readOnly && onRemoveRule ? (
				<Dialog
					open={isRemovingBlock}
					onClose={cancelRemoveBlock}
					title="Remove Rule"
					sm
				>
					<Dialog.Body>
						<Text>
							Are you sure you want to Remove this Date Range Rules from this
							Policy?
						</Text>
					</Dialog.Body>
					<Dialog.Footer>
						<Inline justifyContent="end">
							<Button
								level="primary"
								status="danger"
								onClick={() => {
									cancelRemoveBlock()
									onRemoveRule()
								}}
							>
								Yes, Remove
							</Button>
						</Inline>
					</Dialog.Footer>
				</Dialog>
			) : null}
		</Stack>
	)
}

export function RuleSelectDialog({
	open,
	title,
	requireFallbackRule,
	selectedRules,
	onClose,
	onSuccess,
}: {
	open: boolean
	title: string
	requireFallbackRule?: boolean
	selectedRules: Array<TCustomerPaymentPreference>
	onClose: () => void
	onSuccess: (rules: Array<TCustomerPaymentPreference>) => void
}) {
	const initialValues = useMemo<TRuleSelectFormSchema>(
		() => ({ rules: keepFallbackRuleLast(selectedRules) }),
		[selectedRules]
	)
	return (
		<Dialog open={open} onClose={onClose} title={title} lg>
			<Dialog.Body>
				<RuleSelectForm
					initialValues={initialValues}
					requireFallbackRule={requireFallbackRule}
					onSubmit={onSuccess}
				/>
			</Dialog.Body>
		</Dialog>
	)
}

function RuleSelectForm({
	initialValues,
	requireFallbackRule,
	onSubmit,
}: {
	initialValues: TRuleSelectFormSchema
	requireFallbackRule?: boolean
	onSubmit: (rules: Array<TCustomerPaymentPreference>) => void
}) {
	const xhr = useXHR()
	const [q, setQ] = useState<string>("")
	const [isReordering, setIsReordering] = useState<boolean>(false)
	const validationSchema = useMemo(
		() => makeRuleSelectSchema(requireFallbackRule),
		[requireFallbackRule]
	)
	const {
		data: rules,
		error,
		isLoading,
		mutate,
	} = useSWR<Array<TCustomerPaymentPreference>, Error>(
		`/customer-payment-preferences?q=${q}`,
		() =>
			customerPaymentPreferencesXHR(xhr)
				.get({ q, limit: 100 })
				.then((resp) => resp.data)
	)

	const filterRuleOptions = useCallback(
		(options?: Array<TCustomerPaymentPreference>, query?: string) => {
			if (!options || !query?.trim()) return options
			const searchQuery = query.toLowerCase().trim()
			return options.filter((rule) => {
				const condition = formatRuleConditionLabel(rule).toLowerCase()
				const creator = (rule.created_by?.name || "").toLowerCase()
				const name = (rule.name || "").toLowerCase()
				return (
					condition.includes(searchQuery) ||
					creator.includes(searchQuery) ||
					name.includes(searchQuery)
				)
			})
		},
		[]
	)

	const hasRules = q || (rules && rules?.length > 0)

	return (
		<Form<TRuleSelectFormSchema>
			initialValues={initialValues}
			validate={validateFormValues(validationSchema)}
			onSubmit={({ rules }) => onSubmit(rules)}
			subscription={{}}
		>
			{({ form, handleSubmit }) => {
				// the api returns the existing rule for duplicate details, so it is
				// neither listed nor selected twice
				function addCreatedRule(rule: TCustomerPaymentPreference) {
					const selected = form.getState().values.rules || []
					const selectedRule = selected.find((r) => isSameRule(r, rule))
					// the loaded copy has `created_by`, which the create response lacks
					const loadedRule =
						selectedRule || (rules || []).find((r) => isSameRule(r, rule))
					const ruleToAdd = loadedRule || rule
					mutate((existing) => putRuleFirst(existing || [], ruleToAdd), {
						revalidate: false,
					})
					if (!selectedRule) form.change("rules", [...selected, ruleToAdd])
					showSnackbar(
						selectedRule
							? "This rule is already selected"
							: loadedRule
								? "A rule with these details already exists, it has been selected"
								: "Rule created successfully"
					)
				}
				return (
					<form
						noValidate
						onSubmit={(e) => {
							// this form is nested inside the policy form
							e.stopPropagation()
							handleSubmit(e)
						}}
					>
						<Stack gap="4">
							<PinFallbackRuleToEnd />
							{hasRules ? (
								<Stack gap="4" style={{ minHeight: "18rem" }}>
									<SelectField
										select={Select}
										name="rules"
										label={
											<Box
												as="span"
												display="block"
												marginBottom="1"
												fontWeight="semibold"
											>
												Select
											</Box>
										}
										placeholder="Type to search..."
										menuPlaceholder="No rules found."
										multiple
										fullWidth
										hideSelected
										disabled={isReordering}
										options={rules || []}
										query={q}
										onQuery={setQ}
										filterOptions={filterRuleOptions}
										isLoading={isLoading}
										optionRenderer={({
											option,
										}: {
											option: TCustomerPaymentPreference
										}) => (
											<Inline
												gap="6"
												alignItems="center"
												flexWrap="wrap"
												flex="1"
												marginLeft="2"
											>
												<Box flex="1" style={{ minWidth: "16rem" }}>
													<RuleDetails rule={option} />
												</Box>
												<Box flex="1" style={{ minWidth: "12rem" }}>
													<InstalmentShareBar breakdowns={option.breakdowns} />
												</Box>
												<CreatedBy rule={option} />
											</Inline>
										)}
									/>
									{error ? (
										<Alert status="error">
											{error.message ||
												"Failed to load the rules. Please try again."}
										</Alert>
									) : null}
									<GetFieldValue<TRuleSelectFormSchema["rules"]> name="rules">
										{({ value, onChange }) => {
											// the rules of this block, in the order they apply
											const selected = value || []
											// the fallback rule stays last, so only the others can
											// be reordered
											const reorderableCount = selected.filter(
												(r) => !isFallbackRule(r)
											).length
											return selected.length ? (
												<Stack gap="2">
													<Inline
														gap="4"
														alignItems="center"
														justifyContent="between"
														flexWrap="wrap"
													>
														<Text color="accent" fontWeight="semibold">
															Selected Rules ({selected.length})
														</Text>
														{reorderableCount > 1 ? (
															<Button
																size="sm"
																level={isReordering ? "primary" : "secondary"}
																onClick={() =>
																	setIsReordering((value) => !value)
																}
															>
																<Icons.SwitchHorizontal rotate="90" />{" "}
																{isReordering
																	? "Done Reordering"
																	: "Reorder Rules"}
															</Button>
														) : null}
													</Inline>
													{isReordering ? (
														<Text color="muted" fontSize="sm">
															Drag the rules to change the order in which they
															are applied.
														</Text>
													) : null}
													<Stack as="ol" gap="2">
														{selected.map((rule, index) => (
															<SelectedRuleRow
																key={rule.id}
																rule={rule}
																index={index}
																isReordering={isReordering}
																isPinned={isFallbackRule(rule)}
																onToggle={() =>
																	onChange(
																		selected.filter(
																			(r) => Number(r.id) !== Number(rule.id)
																		)
																	)
																}
																onMove={(from, to) =>
																	onChange(moveRule(selected, from, to))
																}
															/>
														))}
													</Stack>
												</Stack>
											) : null
										}}
									</GetFieldValue>
								</Stack>
							) : !isLoading ? (
								<Stack
									flex="1"
									alignItems="center"
									justifyContent="center"
									gap="4"
									paddingY="10"
								>
									<Icons.Info color="muted" size="6" />
									<Text color="muted">No rules found</Text>
									<NewPaymentRuleInDialog onSuccess={addCreatedRule}>
										{({ onClick }) => (
											<Button
												level="secondary"
												onClick={onClick}
												disabled={isReordering}
												style={{ opacity: isReordering ? 0.5 : 1 }}
											>
												+ Create Rule
											</Button>
										)}
									</NewPaymentRuleInDialog>
								</Stack>
							) : (
								<Inline
									minWidth="full"
									height="32"
									alignItems="center"
									justifyContent="center"
								>
									<Spinner />
								</Inline>
							)}
							{hasRules ? (
								<>
									<Divider marginY="0" />
									<Inline
										gap="4"
										alignItems="center"
										justifyContent="between"
										flexWrap="wrap"
									>
										<NewPaymentRuleInDialog onSuccess={addCreatedRule}>
											{({ onClick }) => (
												<Button
													level="secondary"
													onClick={onClick}
													disabled={isReordering}
													style={{ opacity: isReordering ? 0.5 : 1 }}
												>
													+ Create New Rule
												</Button>
											)}
										</NewPaymentRuleInDialog>
										<Button
											type="submit"
											level="primary"
											status="primary"
											disabled={isReordering}
											style={{ opacity: isReordering ? 0.5 : 1 }}
										>
											Add Rule
										</Button>
									</Inline>
								</>
							) : null}
						</Stack>
					</form>
				)
			}}
		</Form>
	)
}

// Rules picked from the search and newly created rules are added at the end,
// after the fallback rule, so it is moved back to the end of the selected rules.
function PinFallbackRuleToEnd() {
	const form = useForm()
	const { value: rules } =
		useFieldValue<TRuleSelectFormSchema["rules"]>("rules")
	useEffect(() => {
		if (!rules) return
		const sorted = keepFallbackRuleLast(rules)
		if (sorted !== rules) form.change("rules", sorted)
	}, [rules, form])
	return null
}

// The rules of a block are applied in order, so the position of a selected
// rule is shown next to it and can be changed by dragging in reorder mode.
// The fallback rule is pinned at the end, so it can neither be dragged nor
// have other rules dropped around it.
function SelectedRuleRow({
	rule,
	index,
	isReordering,
	isPinned,
	onToggle,
	onMove,
}: {
	rule: TCustomerPaymentPreference
	index: number
	isReordering: boolean
	isPinned: boolean
	onToggle: () => void
	onMove: (from: number, to: number) => void
}) {
	const canDrag = isReordering && !isPinned
	const dragIndex = useRef<number | null>(null)
	const [dropPosition, setDropPosition] = useState<"above" | "below" | null>(
		null
	)

	return (
		<Inline
			as="li"
			gap="3"
			alignItems="center"
			style={{ position: "relative" }}
		>
			{dropPosition ? (
				<Box
					bgColor="primary_emphasis"
					rounded="md"
					style={{
						position: "absolute",
						left: 0,
						right: 0,
						height: "2px",
						top: dropPosition === "above" ? "-0.25rem" : undefined,
						bottom: dropPosition === "below" ? "-0.25rem" : undefined,
					}}
				/>
			) : null}
			<Box
				size="5"
				rounded="full"
				borderWidth="1"
				borderColor="black"
				display="flex"
				alignItems="center"
				justifyContent="center"
				flexShrink="0"
			>
				<Text fontSize="sm" color="black">
					{index + 1}
				</Text>
			</Box>
			<Box
				flex="1"
				padding="3"
				rounded="md"
				bgColor="primary"
				borderWidth="1"
				draggable={canDrag}
				style={canDrag ? { cursor: "grab" } : undefined}
				onDragStart={
					canDrag
						? (e: React.DragEvent) => {
								dragIndex.current = index
								e.dataTransfer.setData("text/plain", String(index))
								e.dataTransfer.effectAllowed = "move"
							}
						: undefined
				}
				onDragOver={
					canDrag
						? (e: React.DragEvent) => {
								e.preventDefault()
								const rect = e.currentTarget.getBoundingClientRect()
								const isOverTopHalf = e.clientY - rect.top < rect.height / 2
								setDropPosition(isOverTopHalf ? "above" : "below")
							}
						: undefined
				}
				onDragLeave={canDrag ? () => setDropPosition(null) : undefined}
				onDrop={
					canDrag
						? (e: React.DragEvent) => {
								e.preventDefault()
								setDropPosition(null)
								const transferred = e.dataTransfer.getData("text/plain")
								const from =
									transferred === "" ? dragIndex.current : Number(transferred)
								if (from === null || isNaN(from) || from === index) return
								const rect = e.currentTarget.getBoundingClientRect()
								const isOverTopHalf = e.clientY - rect.top < rect.height / 2
								const rawTarget = isOverTopHalf ? index : index + 1
								onMove(from, from < rawTarget ? rawTarget - 1 : rawTarget)
							}
						: undefined
				}
			>
				<Inline gap="6" alignItems="center" flexWrap="wrap">
					{isReordering ? (
						isPinned ? (
							<Tooltip content="Fallback Rule always applies last">
								<Box color="muted">
									<Icons.Lock />
								</Box>
							</Tooltip>
						) : (
							<Box color="muted">
								<Icons.ArrowRightLeft rotate="90" />
							</Box>
						)
					) : (
						<Box>
							<CheckboxInput checked onChange={onToggle} />
						</Box>
					)}
					<Box flex="1" style={{ minWidth: "16rem" }}>
						<RuleDetails rule={rule} />
					</Box>
					<Box flex="1" style={{ minWidth: "12rem" }}>
						<InstalmentShareBar breakdowns={rule.breakdowns} />
					</Box>
					<CreatedBy rule={rule} />
				</Inline>
			</Box>
		</Inline>
	)
}

function CreatedBy({ rule }: { rule: TCustomerPaymentPreference }) {
	return (
		<Stack style={{ minWidth: "8rem" }}>
			<Text>{rule.created_by?.name || "You"}</Text>
			<RelativeTime timestamp={rule.created_at} color="muted" fontSize="sm" />
		</Stack>
	)
}

export function RuleDetails({ rule }: { rule: TCustomerPaymentPreference }) {
	const instalments = formatRuleInstalmentLabels(rule)
	return (
		<Stack gap="1">
			<Text
				fontWeight="semibold"
				color={isFallbackRule(rule) ? "success" : undefined}
			>
				{formatRuleConditionLabel(rule)}
			</Text>
			<MinimumAmountBadge value={rule.min_first_instalment_amounts} />
			{instalments.length ? (
				<Text color="muted">{joinAttributes(...instalments)}</Text>
			) : null}
		</Stack>
	)
}
